/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabrielecavallo
 * File: DrawingAreaFeature.ts
 * Project: mle-client-n
 * Committed last: 2022/11/22 @ 166
 * ---
 * Description:
 */

import {StageFeature} from '../common/StageFeature';
import {calculateCanvasHeight, calculateCanvasWidth} from '../../../common/utils';
import {Editor} from '../../observables/Editor';
import {action, computed, makeObservable, observable, reaction} from 'mobx';
import Pamela from '@revodigital/pamela';
import {MeasureUnits, ShapeTypes} from '../../../common/enums';
import {Size2D, sizeOf} from '@revodigital/pamela/lib/common/Size2D';
import {ObservableText} from '../../observables/ObservableText';
import {EDITING_END} from '@revodigital/pamela/lib/events/text/EditingEnd';
import {EDITING_START} from '@revodigital/pamela/lib/events/text/EditingStart';
import {HorizontalAlignment, VerticalAlignment} from '@revodigital/pamela/lib/configuration/Alignment';
import {ObservableShape} from '../../observables/ObservableShape';

export const DRAW_AREA_FNAME = 'drawing-area';
export const AREA_LAYER = 'background';
export const LABEL_PAPER = 'label-paper';
export const LABEL_INDICATOR = 'label-size';

export class DrawingAreaFeature extends StageFeature<void> {
  @observable
  private _areaBackground: string;

  constructor(parent: Editor) {
    super(DRAW_AREA_FNAME, parent, true);
    this._areaBackground = '#F5F8FA';

    makeObservable(this);

    // Update selected shape text
    this.target.on(EDITING_END,
      (event: any) => this._onTextEditingEnd(event.node.text()));

    this.target.on(EDITING_START,
      (event: any) => this._onTextEditingStart(event.node));


    Pamela.Barcode.prototype.invalidCodeMessage = 'Contenuto codice non valido';

    // Updates measure unit indicator when it changes
    reaction(() => this.sourcePointer.projectData.projectOptions.measureUnit,
        (value) => {
          let sizeInd = this.target.getLayerByName(AREA_LAYER)?.getChildWithName(
              LABEL_INDICATOR) as Pamela.Text;

          if (sizeInd) {
            sizeInd.text(this._formatIndicatorText(this.sourcePointer.labelSize,
                value));
          }
        });

    // Update indicator and text rectangle on size change
    reaction(() =>
        this.sourcePointer.labelSize, (size) => {
      const mu = this.sourcePointer.projectData.projectOptions.measureUnit;

      let paper = this.target.getLayerByName(AREA_LAYER)?.getChildWithName(
          LABEL_PAPER);
      let sizeInd = this.target.getLayerByName(AREA_LAYER)?.getChildWithName(
          LABEL_INDICATOR) as Pamela.Text;

      const projectConfig = this.sourcePointer.projectData;

      if (paper) {
        paper.width(projectConfig.labelWidth);
        paper.height(projectConfig.labelHeight);
      }

      if (sizeInd)
        sizeInd.text(this._formatIndicatorText(size, mu));
    });
  }

  /**
   * Allinea la forma corrente verticalmente
   * @param align
   */
  public alignCurrentShapeVertically(align: VerticalAlignment) {
    const shape = this.sourcePointer.getSelectedShape<ObservableShape>();
    const labelSize = this.sourcePointer.labelSize;
    const shapeHeightPx = this.sourcePointer.currentToPixel(shape.height);

    let pxValue = 0;
    switch (align) {
      case VerticalAlignment.Center:
        pxValue = (labelSize.getHeight() / 2) - (shapeHeightPx / 2);
        break;
      case VerticalAlignment.Top:
        pxValue = 0;
        break;
      case VerticalAlignment.Bottom:
        pxValue = labelSize.getHeight() - shapeHeightPx;
        break;
      default:
        break;
    }

    shape.setYPixels(pxValue, { applyToPamelaShape: true });
  }

  /**
   * Allinea la forma corrente orizzontalmente
   * @param align
   */
  public alignCurrentShapeHorizontally(align: HorizontalAlignment) {
    const shape = this.sourcePointer.getSelectedShape<ObservableShape>();
    const labelSize = this.sourcePointer.labelSize;
    const shapeWidthPx = this.sourcePointer.currentToPixel(shape.width);

    let pxValue = 0;
    switch (align) {
      case HorizontalAlignment.Center:
        pxValue = (labelSize.getWidth() / 2) - (shapeWidthPx / 2);
        break;
      case HorizontalAlignment.Right:
        pxValue = labelSize.getWidth() - shapeWidthPx;
        break;
      case HorizontalAlignment.Left:
        pxValue = 0;
        break;
      default:
        break;
    }

    shape.setXPixels(pxValue, {applyToPamelaShape: true});
  }

  private _onTextEditingEnd(newText: string) {
    if (this.sourcePointer && this.sourcePointer.getSelectedShape())
      this.sourcePointer.getSelectedShape<ObservableText>().setText(newText);
  }

  private _onTextEditingStart(node: Pamela.Text) {
    this.sourcePointer.select(node);
  }

  /**
   * Get background color of the stage
   */
  @computed
  public get backgroundColor(): string {
    return this._areaBackground;
  }

  @action
  public setBackgroundColor(color: string) {
    this._areaBackground = color;
    this.target.content.style.background = color;
  }

  destroy(): boolean {
    return false;
  }

  private _initAreaLayer() {
    if (!this.target.hasLayerWithName(AREA_LAYER)) {
      // Create background layer
      const bg = new Pamela.Layer({
        name: AREA_LAYER,
        listening: false,
        draggable: false,
      });

      this.target.addLayerBeforeAll(bg);
    }
  }

  private _addLabelPaper() {
    const projectConfig = this.sourcePointer.projectData;
    const bgLayer = this.target.getLayerByName(AREA_LAYER);

    if (!bgLayer) {
      this._initAreaLayer();
      this._addLabelPaper();
      return;
    }

    if (!bgLayer.getChildWithName(LABEL_PAPER)) {
      bgLayer.add(new Pamela.Rect({
        name: LABEL_PAPER,
        id: this.sourcePointer.generateShapeId(ShapeTypes.RECT),
        x: 0,
        y: 0,
        width: projectConfig.labelWidth,
        height: projectConfig.labelHeight,
        fill: 'white',
        stroke: '#30404D',
        strokeWidth: 1,
        listening: false,
        draggable: false,
      }));
    }
  }

  private _addLabelSizeIndicator() {
    const projectConfig = this.sourcePointer.projectData;
    const bgLayer = this.target.getLayerByName(AREA_LAYER);
    // Get label size
    const size = sizeOf(this.sourcePointer.projectData.labelWidth,
        this.sourcePointer.projectData.labelHeight);
    const measureUnit = this.sourcePointer.projectData.projectOptions.measureUnit;

    if (!bgLayer) {
      this._initAreaLayer();
      this._addLabelPaper();
      return;
    }

    if (!bgLayer.getChildWithName(LABEL_INDICATOR)) {
      bgLayer.add(new Pamela.Text({
        editable: false,
        name: LABEL_INDICATOR,
        x: 0,
        y: -32,
        text: this._formatIndicatorText(size, measureUnit),
        fontSize: 15,
        fill: '#30404D',
        listening: false,
        draggable: false,
      }));
    }
  }

  private _formatIndicatorText(pxSize: Size2D, mu: MeasureUnits): string {
    return `${this.sourcePointer.pixelToCurrent(pxSize.getWidth())}x${this.sourcePointer.pixelToCurrent(
        pxSize.getHeight())}${mu}`;
  }

  private _hasLabelPaper(): boolean {
    return this.target.getLayerByName(AREA_LAYER)?.getChildWithName(LABEL_PAPER) !== undefined;
  }

  init(): boolean {
    let stage = this.target;
    // Apply background color
    stage.content.style.background = this._areaBackground;

    // Init static layer for area paper
    this._initAreaLayer();
    this._addLabelPaper();
    this._addLabelSizeIndicator();

    // Init stage size following available size
    stage.width(calculateCanvasWidth() as number);
    stage.height(calculateCanvasHeight() as number);

    // Aggiungo gli eventi listener per ridimensionare il canvas
    window.addEventListener(('resize'), () => {
      stage.width(calculateCanvasWidth() as number);
      stage.height(calculateCanvasHeight() as number);
    });
    return false;
  }
}